import { useMemo } from 'react';

import { Permission, hasPermission } from '@bookora/shared';
import { Grid } from '@bookora/ui';

import { useAuth } from '@/auth/token-store';
import { useOnboarding } from '@/features/settings/hooks';
import { useCan } from '@/permissions/use-can';

import { ownerVariant, resolvePersona } from './persona';
import { useSetupProgress } from './setup-progress';
import type { StepContext, Viewport } from './tours/types';

/** `wide` from `md` (768px) up; `compact` below it. Unknown (first render) is wide. */
export function useViewport(): Viewport {
  const screens = Grid.useBreakpoint();
  return screens.md === false ? 'compact' : 'wide';
}

/** Everything a tour step's copy and filters may read about the viewer. */
export function useStepContext(): StepContext {
  const { permissions, activeClinic } = useAuth();
  const viewport = useViewport();
  const canEditSettings = useCan(Permission.SETTINGS_EDIT);
  const onboarding = useOnboarding();
  // The same reading of the checklist as the dashboard card, the hub row and
  // the setup bar (`useSetupProgress`): a checklist marked complete is not
  // «incomplete» because an optional-turned-required step is still open.
  const setup = useSetupProgress();
  const onboardingIncomplete = canEditSettings && onboarding.data ? setup.visible : null;
  // Where «ادامهٔ راه‌اندازی» (the core tour's finish) goes: the next open
  // step's own page. Read by the tour content as `ctx.nextSetupPath`.
  const nextSetupPath = onboardingIncomplete ? setup.nextPath : null;

  return useMemo(() => {
    const now = new Date();
    const clinicType = activeClinic?.clinicType === 'SOLO_DOCTOR' ? 'SOLO_DOCTOR' : 'CLINIC';
    const membership = activeClinic
      ? { isDoctor: activeClinic.isDoctor, joinedAt: activeClinic.joinedAt }
      : null;
    const persona = resolvePersona(permissions, membership, clinicType, now);
    return {
      persona,
      ownerVariant: ownerVariant(persona, membership, clinicType, now),
      permissions,
      viewport,
      clinicType,
      onboardingIncomplete,
      nextSetupPath,
      can: (p: Permission) => hasPermission(permissions, p),
    };
  }, [permissions, activeClinic, viewport, onboardingIncomplete, nextSetupPath]);
}
